import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';

import { useSelector, useDispatch } from '../../store/utils';
import { variablesChange } from '../../store/actions';
import { DialogClose } from '../../atoms/dialog-close';

type Props = {
  sx?: SxProps;
};

export const RfidCardCreate = ({ sx = {} }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const rfidCards = useSelector((state) => state.variables.RFID_CARDS);
  const rfidLastCard = useSelector((state) => state.variables.RFID_LAST_CARD);
  const rfidLastCardAt = useSelector(
    (state) => state.variables.RFID_LAST_CARD_AT
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [scannedId, setScannedId] = useState<string | null>(null);
  const [cardName, setCardName] = useState('');
  // Snapshot of RFID_LAST_CARD_AT at the moment the dialog was opened
  const lastCardAtOnOpen = useRef<string | undefined>(undefined);

  const handleOpenDialog = () => {
    lastCardAtOnOpen.current = rfidLastCardAt;
    setScannedId(null);
    setCardName('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setScannedId(null);
    setCardName('');
  };

  // Watch for a new card scan while the dialog is open
  useEffect(() => {
    if (!dialogOpen) return;
    if (!rfidLastCardAt) return;
    if (rfidLastCardAt === lastCardAtOnOpen.current) return;
    setScannedId(rfidLastCard ?? null);
  }, [dialogOpen, rfidLastCard, rfidLastCardAt]);

  const handleSave = () => {
    if (!scannedId) return;
    dispatch(
      variablesChange({
        RFID_CARDS: {
          ...rfidCards,
          [scannedId]: {
            name: cardName.trim(),
            createdAt: new Date().toISOString(),
          },
        },
      })
    );
    handleCloseDialog();
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpenDialog} sx={sx}>
        <FormattedMessage
          id="wallbox_rfid.add_button"
          defaultMessage="Přidat RFID kartu"
        />
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <FormattedMessage
            id="wallbox_rfid.dialog.title"
            defaultMessage="Přidat RFID kartu"
          />
        </DialogTitle>
        <DialogClose onClose={handleCloseDialog} />

        <DialogContent>
          {scannedId === null ? (
            <Stack alignItems="center" gap={2} py={2}>
              <CircularProgress />
              <Typography>
                <FormattedMessage
                  id="wallbox_rfid.dialog.waiting"
                  defaultMessage="Přiložte kartu ke čtečce…"
                />
              </Typography>
            </Stack>
          ) : rfidCards?.[scannedId] ? (
            <Stack alignItems="center" gap={2} py={2}>
              <Typography color="warning.main">
                <FormattedMessage
                  id="wallbox_rfid.dialog.already_exists"
                  defaultMessage="Karta {id} je již uložena pod názvem {name}."
                  values={{
                    id: <strong>{scannedId}</strong>,
                    name: rfidCards[scannedId].name,
                  }}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage
                  id="wallbox_rfid.dialog.already_exists_hint"
                  defaultMessage="Přiložte jinou kartu nebo zavřete dialog."
                />
              </Typography>
            </Stack>
          ) : (
            <Stack gap={2} pt={1}>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage
                  id="wallbox_rfid.dialog.scanned_id"
                  defaultMessage="ID karty: {id}"
                  values={{ id: <strong>{scannedId}</strong> }}
                />
              </Typography>
              <TextField
                label={intl.formatMessage({
                  id: 'wallbox_rfid.dialog.name_label',
                  defaultMessage: 'Název karty',
                })}
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                autoFocus
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>
            <FormattedMessage
              id="wallbox_rfid.dialog.cancel"
              defaultMessage="Zrušit"
            />
          </Button>
          <Button
            variant="contained"
            disabled={
              scannedId === null ||
              !!rfidCards?.[scannedId] ||
              cardName.trim() === ''
            }
            onClick={handleSave}
          >
            <FormattedMessage
              id="wallbox_rfid.dialog.save"
              defaultMessage="Uložit"
            />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
